import React, {FormEvent, MouseEvent, useCallback, useRef, useState} from "react";
import intl from "react-intl-universal";
import {useAppState, useRootStore} from "@stores/AppStateContext";
import {useAuthServices} from "../../../../../../platform/hooks/useServiceCombinations";

import {getInviteInfoFromLink, InviteInfo} from "../../../../../../actions/invite";

import {LoginButton} from "./LoginButton";
import {AuthModalHeader} from "../../AuthModal";
import {MaterialUiInput} from "../../../../../components/form/material-ui-input/MaterialUiInput";
import {Button} from "../../../../../components/Button";
import {SocialSignInRow} from "../../components/SocialSignInRow";
import {signUp} from "../../common/signUp";


import {pageAdditionalParamsRef} from "../../../../../../volatile/pageAdditionalParams";
import {autoLogin} from "../../common/autoLogin";
import {getUTMFromCookie} from "../../../../../../utils/googleTagManager";


export interface SignupMeta extends Partial<InviteInfo> {
    utm?: string;
    captchaToken?: string;
    company_name?: string;
}

export interface SignUpData {
    username: string;
    password: string;
    options?: {
        validationData?: InviteInfo;
        clientMetadata?: SignupMeta;
    }
}

interface FormErrors {
    email?: string | React.ReactElement;
    password?: string;
    password_confirm?: string;
}

export interface SignUpAdditionalProps {
    emailDefaultValue?: string;
    passwordDefaultValue?: string;
}

export const SignUpFirstStep = () => {
    const appState = useAppState();
    const rootStore = appState.rootStore;
    const authServices = useAuthServices();
    const [errors, setErrors] = useState<FormErrors>({});
    const [showNewPassword, setShowNewPassword] = useState(false);
    const [showNewPasswordConfirm, setShowNewPasswordConfirm] = useState(false);
    const [isLoading, setIsLoading] = useState(false);
    const isSubmittingRef = useRef(false);

    const {emailDefaultValue, passwordDefaultValue} = pageAdditionalParamsRef.current as SignUpAdditionalProps;
    const [email, setEmail] = useState(emailDefaultValue ? emailDefaultValue : '');
    const [password, setPassword] = useState(passwordDefaultValue ? passwordDefaultValue : '');
    const [passwordConfirm, setPasswordConfirm] = useState(passwordDefaultValue ? passwordDefaultValue : '');
    const [companyName, setCompanyName] = useState('');

    const handleLogInClick = useCallback((event: MouseEvent) => {
        event.preventDefault();
        rootStore.view.openPageByName('sign-in', {}, {}, {
            emailDefaultValue: email.trim(),
            passwordDefaultValue: password.trim(),
        });
    }, [email, password]);

    const handleSubmit = async (event: FormEvent<HTMLFormElement>) => {
        event.preventDefault();
        if (isSubmittingRef.current) {
            return;
        }
        isSubmittingRef.current = true;

        if (passwordConfirm !== password) {
            setErrors({password_confirm: intl.get('sign_up.confirm_pwd_not_match').d("The passwords you entered don't match.")})
            isSubmittingRef.current = false;
            return;
        } else {
            setErrors({});
        }
        setIsLoading(true);

        const inviteInfo = getInviteInfoFromLink();
        const signUpParams: SignUpData = {
            username: email,
            password: password,
            options: {
                validationData: inviteInfo,
            },
        };
        if (inviteInfo !== null) {
            signUpParams.options!.clientMetadata = inviteInfo;
        }
        const utm = getUTMFromCookie();
        if (utm) {
            signUpParams.options!.clientMetadata = {
                ...signUpParams.options!.clientMetadata,
                utm: JSON.stringify(utm)
            };
        }

        const honeypotValue = companyName.trim();
        if (honeypotValue) {
            signUpParams.options!.clientMetadata = {
                ...signUpParams.options!.clientMetadata,
                company_name: honeypotValue,
            };
        }

        const successfulSignup = await signUp(authServices.authService, signUpParams as any, setErrors);
        if (successfulSignup) {
            await autoLogin(appState, rootStore.view, signUpParams.username, signUpParams.password);
        }
        isSubmittingRef.current = false;
        setIsLoading(false);
    };

    const terms_link = `<a href="/terms-and-conditions" class="TextLink" target="_blank">${intl.get('login.terms_link').d('Terms of Use')}</a>`;
    const privacy_link = `<a href="/privacy-policy" class="TextLink" target="_blank">${intl.get('login.privacy_link').d('Privacy Policy')}</a>`;
    const termsHtmlString = intl.getHTML('login.policy_statement', {
        terms_link,
        privacy_link,
    }).d(`<span>By proceeding to create your account and use Logycore, you are agreeing our ${terms_link} and ${privacy_link}.</span>`);

    return (
        <form onSubmit={handleSubmit}>
            <AuthModalHeader title={intl.get('sign_up.header').d('Create an account')}/>
            <MaterialUiInput
                key="emailField"
                className={'Auth-input AuthModalBodyStart'}
                isRequired={true}
                name="email"
                label={intl.get('sign_up.email').d('Email')}
                fullWidth={true}
                error={errors.email}
                value={email}
                onChange={(value) => setEmail(value.trim())}
            />
            <MaterialUiInput
                isRequired={true}
                className={'Auth-input'}
                name="password"
                type="password"
                autoComplete={"off"}
                showPassword={showNewPassword}
                setShowPassword={setShowNewPassword}
                label={intl.get('sign_up.password').d('Password')}
                fullWidth={true}
                error={errors.password}
                value={password}
                onChange={(value) => setPassword(value.trim())}
            />
            <MaterialUiInput
                isRequired={true}
                className={'Auth-input'}
                name="password_confirm"
                type="password"
                autoComplete={"off"}
                showPassword={showNewPasswordConfirm}
                setShowPassword={setShowNewPasswordConfirm}
                label={intl.get('sign_up.password_confirm').d('Confirm password')}
                fullWidth={true}
                error={errors.password_confirm}
                value={passwordConfirm}
                onChange={(value) => setPasswordConfirm(value.trim())}
            />
            <div className="Auth-pot" aria-hidden="true">
                <label htmlFor="signup-company-name">Company name</label>
                <input
                    id="signup-company-name"
                    name="company_name"
                    autoComplete="organization"
                    tabIndex={-1}
                    value={companyName}
                    onChange={(event) => setCompanyName(event.target.value)}
                />
            </div>
            <Button
                style={'solid'}
                type="primary"
                htmlType="submit"
                fullWidth={true}
                className={'Auth-input Auth-btn'}
                text={intl.get('general.continue').d('Continue')}
                size={'lg'}
                loading={isLoading}
            />
            <SocialSignInRow lineText={intl.get('social-sign-in.line.signup').d('or sign up with')}/>
            <div className="Auth-policy-info body-medium">
                {termsHtmlString}
            </div>
            <LoginButton handleLogInClick={handleLogInClick}/>
        </form>
    );
}
